import { RouteIds } from '../types';
import { combineLatest, Observable } from 'rxjs';
import { GoogleAnalyticsParametersType, GoogleAnalyticsQuoteToolLiteQuote } from './google-analytics-parameters.type';
import { inject, Injectable } from '@angular/core';
import { QuoteDataLayerStrategy, QuoteFieldsBuilders } from './quote-data-layer.strategy';
import { map } from 'rxjs/operators';
import { QuoteStore, QuoteToolLiteStore } from '../store';
import { QuoteDataLayerService } from './quote-data-layer.service';
import { YesNo } from '@lx/quote-tool/shared/google-analytics';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';

@Injectable({
  providedIn: 'root',
})
export class JuniorQuoteDataLayerStrategy implements QuoteDataLayerStrategy {
  readonly flow: Flow = Flows.Junior;
  quoteFieldsBuilders: QuoteFieldsBuilders | undefined;

  private readonly quoteStore = inject(QuoteStore);
  private readonly quoteDataLayerService = inject(QuoteDataLayerService);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);

  initializeDataLayerStepsBuilders(): void {
    this.quoteFieldsBuilders = {
      [RouteIds.course]: this.getQuoteBaseDataBuilder(),
      [RouteIds.extras]: this.getQuoteBaseDataBuilder(),
      [RouteIds.applicant]: this.getQuoteBaseDataBuilder(),
      [RouteIds.quote]: this.getAllQuoteRelatedDataBuilder(),
      [RouteIds.booking]: this.getAllQuoteRelatedDataBuilder(),
      [RouteIds.confirmation]: this.getAllQuoteRelatedDataBuilder(),
      [RouteIds.payment]: this.getAllQuoteRelatedDataBuilder(),
    };
  }

  buildDataLayer(): Observable<Partial<GoogleAnalyticsParametersType>> {
    return this.quoteDataLayerService.buildQuoteDataLayerObject(this.quoteFieldsBuilders);
  }

  /**
   * Returns a function with the selectors that retrieves base data fields such as language, school, accommodation type, program of interest...
   * and build the data layer for the initial steps in the quote process.
   */
  private getQuoteBaseDataBuilder() {
    return () => this.getQuoteBaseData();
  }

  /**
   * Returns a function with the selectors that retrieves quote-related fields such as premium activities, private lessons,
   * insurance, transfers, and prices. This function is used to build the data layer for specific steps in the quote process.
   * And build the data layer for the steps where all quote fields are available.
   */

  private getAllQuoteRelatedDataBuilder() {
    return () =>
      combineLatest([
        this.getQuoteBaseData(),
        this.quoteDataLayerService.getInsuranceData(),
        this.quoteDataLayerService.getTransfersData(),
        this.getPremiumActivitiesData(),
        this.getPrivateLessonsData(),
        this.getUnaccompaniedMinorsData(),
        this.getPremiumResidenceData(),
        this.quoteDataLayerService.getPricesData(),
      ]).pipe(
        map(([baseData, insuranceData, transfersData, premiumActivitiesData, privateLessons, unaccompaniedMinors, premiumAccommodation, prices]) => ({
          ...baseData,
          ...transfersData,
          ...insuranceData,
          ...premiumActivitiesData,
          ...privateLessons,
          ...premiumAccommodation,
          ...unaccompaniedMinors,
          ...prices,
        })),
      );
  }

  private getPremiumActivitiesData(): Observable<Partial<GoogleAnalyticsQuoteToolLiteQuote>> {
    return this.quoteStore.premiumActivities$.pipe(
      map((premiumActivities) => {
        if (!premiumActivities?.length || !Array.isArray(premiumActivities)) {
          return {
            quote_premium_activities: YesNo.No,
          };
        }

        const { name, units } = premiumActivities.at(-1);
        return {
          quote_premium_activities: YesNo.Yes,
          quote_premium_activities_type: name,
          quote_premium_activities_weeks: units,
        };
      }),
    );
  }

  private getPrivateLessonsData(): Observable<Partial<GoogleAnalyticsQuoteToolLiteQuote>> {
    return this.quoteStore.privateLessons$.pipe(
      map((privateLessons) => {
        if (!privateLessons) {
          return {
            quote_private_lessons: YesNo.No,
          };
        }

        return {
          quote_private_lessons: YesNo.Yes,
          quote_private_lessons_weeks: privateLessons?.units,
        };
      }),
    );
  }

  private getUnaccompaniedMinorsData(): Observable<Partial<GoogleAnalyticsQuoteToolLiteQuote>> {
    return this.quoteStore.unaccompaniedMinorServices$.pipe(
      map((unaccompaniedMinorServices) => {
        if (!unaccompaniedMinorServices?.length) {
          return {
            quote_junior_unaccompanied_service: YesNo.No,
          };
        }

        return {
          quote_junior_unaccompanied_service: YesNo.Yes,
        };
      }),
    );
  }

  private getPremiumResidenceData(): Observable<Partial<GoogleAnalyticsQuoteToolLiteQuote>> {
    return combineLatest([this.quoteStore.premiumResidence$, this.quoteToolLiteStore.selectedDuration$]).pipe(
      map(([insurance, duration]) => {
        if (!insurance) {
          return {
            quote_premium_residence: YesNo.No,
          };
        }

        return {
          quote_premium_residence: YesNo.Yes,
          quote_premium_residence_weeks: duration,
        };
      }),
    );
  }

  private getQuoteBaseData() {
    return combineLatest([this.quoteDataLayerService.getBaseData(), this.quoteToolLiteStore.selectionForJuniorFlowDataLayer$]).pipe(
      map(([baseData, { selectedLanguage, selectedSchoolLocation, upselling, selectedStartDate, selectedEndDate }]) => ({
        ...baseData,
        quote_language_interest: selectedLanguage,
        quote_school_interest: selectedSchoolLocation?.schoolName,
        quote_upselling: upselling ? YesNo.Yes : YesNo.No,
        quote_start_date: selectedStartDate,
        quote_end_date: selectedEndDate,
      })),
    );
  }
}
